import {
  Button,
  Center,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { t } from "@lingui/macro";
import {
  type ProjectSimple,
  useProjectsSimpleQuery,
} from "@src/__generated__/urql-graphql";
import { TruncatedTextWithTooltip } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { debounce } from "lodash";
import { observer } from "mobx-react-lite";
import Link from "next/link";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

export const ProjectSearch = observer(function ProjectSearch() {
  const [projects, setProjects] = useState<
    Array<
      Omit<
        ProjectSimple,
        "availableTimeTrackingWorkTypes" | "brand" | "is_pro_bono"
      >
    >
  >([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const wrapperElement = document.getElementById("projects-wrapper");
  const [{ fetching, data }, fetchProjects] = useProjectsSimpleQuery({
    pause: true,
    variables: {
      withProBono: true,
    },
  });

  const searchedProjects = useMemo(() => {
    const lowerCaseSearch = search.toLowerCase();

    return projects.filter(({ title, code }) => {
      const lowerCaseTitle = title.toLowerCase();
      const lowerCaseCode = code.toLowerCase();

      return (
        lowerCaseTitle.includes(lowerCaseSearch) ||
        lowerCaseCode.includes(lowerCaseSearch)
      );
    });
  }, [projects, search]);

  const filteredProjects = useMemo(() => {
    return searchedProjects.filter((_, index) => index < page * 15);
  }, [searchedProjects, search, page]);

  const handleKeypress = useCallback((event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      handleOpen();
    }
  }, []);

  const handleScroll = useCallback(
    debounce((event: Event) => {
      const target = event.target as HTMLElement;
      // -5 cause on mobile device it doesn't scroll all the way down sometimes
      const scrolledToBottom =
        target.offsetHeight + target.scrollTop >= target.scrollHeight - 5;

      if (scrolledToBottom) {
        setPage((prev) => prev + 1);
      }
    }, 250),
    [wrapperElement],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeypress);

    return () => {
      document.removeEventListener("keydown", handleKeypress);
    };
  }, [handleKeypress]);

  useEffect(() => {
    if (!wrapperElement) return;
    wrapperElement?.addEventListener("scroll", handleScroll);

    return () => {
      wrapperElement?.removeEventListener("scroll", handleScroll);
    };
  }, [wrapperElement, handleScroll]);

  useEffect(() => {
    if (!data?.projectsSimpleMap.length) return;
    if (projects.length) return;

    setProjects(data.projectsSimpleMap);
  }, [data?.projectsSimpleMap, projects]);

  const handleOpen = () => {
    onOpen();
    if (projects.length) return;
    fetchProjects();
  };

  const handleClose = () => {
    onClose();
    setSearch("");
    setPage(1);
  };

  return (
    <Fragment>
      <IconButton
        aria-label={t`show project search`}
        icon={<Icon name="search-md" w="6" h="6" />}
        onClick={handleOpen}
        variant="ghost"
      />
      <Modal
        blockScrollOnMount
        isOpen={isOpen}
        onClose={handleClose}
        size="2xl"
        trapFocus={false}
      >
        <ModalOverlay />

        <ModalContent overflow="hidden">
          <ModalBody
            overflowX="hidden"
            overflowY="scroll"
            minH="440"
            maxH="440"
            p="0"
            id="projects-wrapper"
          >
            <HStack
              pos="sticky"
              zIndex="1"
              top="0"
              right="0"
              left="0"
              p="6"
              bg="white"
              spacing="2"
            >
              <Icon name="search-md" />

              <Input
                autoFocus
                onChange={({ target }) => {
                  setSearch(target.value);
                  setPage(1);

                  wrapperElement?.scrollTo?.({
                    top: 0,
                    behavior: "smooth",
                  });
                }}
                placeholder={t`Search for project`}
                variant="unstyled"
              />
              {fetching && <Spinner color="blue.500" size="sm" />}
            </HStack>

            <VStack justify="start" mb="6" px="6">
              {filteredProjects.map((project) => (
                <Link
                  key={`${project.id}-${project.title}`}
                  href={{
                    pathname: "/projects/detail",
                    query: {
                      id: project.id,
                    },
                  }}
                  onClick={onClose}
                  style={{
                    width: "100%",
                  }}
                >
                  <Button
                    justifyContent="start"
                    w="full"
                    h="9"
                    minH="9"
                    pl="6"
                    fontWeight="normal"
                    onClick={onClose}
                    variant="ghost"
                  >
                    <TruncatedTextWithTooltip
                      text={`${project.code} - ${project.title}`}
                    />
                  </Button>
                </Link>
              ))}

              {searchedProjects.length &&
                filteredProjects.length < searchedProjects.length && (
                  <Center minH="6" py="4">
                    <Spinner color="blue.500" size="lg" />
                  </Center>
                )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Fragment>
  );
});
